import React from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import Header from "../components/Header";
import "../styles/PartnerPage.css";
import Footer from "../components/Footer";
import { useDonate } from "../contexts/DonateContext";

export default function JaxPartnershipPage() {
  const { openDonate } = useDonate();

  return (
    <>
      <Header />

      <main className="partner-page partner-page--jax">
        <section className="partner-hero">
          <Container>
            <Row className="align-items-center">
              <Col lg={7}>
                <h1 className="partner-hero__title">Jackson Laboratory Partnership</h1>
                <p className="partner-hero__tagline">
                  Helping turn cutting-edge Alzheimer&apos;s research in Maine
                  into earlier answers for families everywhere.
                </p>
                <div className="partner-hero__actions">
                  <a href="#how-we-partner" className="partner-hero__link">
                    How we&apos;ll partner →
                  </a>
                  <Button
                    variant="primary"
                    className="partner-hero__donate"
                    onClick={() => openDonate({ startOnContact: false })}
                  >
                    Support this research
                  </Button>
                </div>
              </Col>
              <Col lg={5} className="partner-hero__logo-col">
                <div className="partner-hero__logo-card">
                  <img
                    src="/img/jax.png"
                    alt="The Jackson Laboratory logo"
                    className="partner-hero__logo"
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="partner-section">
          <Container>
            <Row>
              <Col lg={{ span: 8, offset: 2 }}>
                <h2 className="partner-section__heading">
                  Who Is The Jackson Laboratory?
                </h2>
                <p className="partner-section__body">
                  The Jackson Laboratory (JAX) is an independent, nonprofit
                  biomedical research institution based in Bar Harbor, Maine.
                  Their scientists use genetics and genomics to understand
                  complex diseases—including cancer, aging, and
                  neurodegenerative disorders like Alzheimer&apos;s—and to turn
                  those discoveries into better diagnostics and treatments.
                </p>
                <p className="partner-section__body">
                  JAX is also one of the world&apos;s leading sources of
                  specialized research mice, which allow labs around the globe
                  to study disease safely and consistently. Behind the scenes,
                  their work shapes a huge share of the medical breakthroughs
                  that eventually reach patients and families.
                </p>
                <p className="partner-section__body partner-section__body--link">
                  Learn more at{" "}
                  <a
                    href="https://www.jax.org/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    jax.org
                  </a>
                  .
                </p>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="partner-section partner-section--alt">
          <Container>
            <Row>
              <Col lg={{ span: 8, offset: 2 }}>
                <h2 className="partner-section__heading">
                  What Are They Doing About Alzheimer&apos;s?
                </h2>
                <p className="partner-section__body">
                  JAX researchers are working to catch Alzheimer&apos;s as early
                  as possible, long before memory loss appears. One of the most
                  promising approaches uses the retina—the light-sensitive
                  tissue at the back of the eye—as a window into brain health.
                  Recent work from JAX connects abnormal retinal blood-vessel
                  changes and specific cell alterations to early dementia risk,
                  and highlights how common genetic variants may show up in the
                  eye before symptoms are felt.
                </p>

                <blockquote className="partner-section__body" style={{ fontStyle: "italic", borderLeft: "4px solid #4338ca", paddingLeft: "0.9rem" }}>
                  “There&apos;s more to the eyes than just simply seeing you. By understanding how the healthy eye ages in different genetic
                  contexts, we may be able to determine people’s risk of developing diseases like Alzheimer’s.”
                  <br />— Gareth Howell, Ph.D.
                </blockquote>

                <blockquote className="partner-section__body" style={{ fontStyle: "italic", borderLeft: "4px solid #4338ca", paddingLeft: "0.9rem" }}>
                  “If it was feasible to go get an eye exam rather than a brain exam, we would have a huge advantage to identify those
                  that were at risk of developing Alzheimer&apos;s.”
                  <br />— Gareth Howell, Ph.D.
                </blockquote>

                <div style={{ marginTop: "1rem" }}>
                  <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: 16, boxShadow: "0 10px 30px rgba(0,0,0,0.12)", background: "#000" }}>
                    <iframe
                      src="https://www.youtube.com/embed/pQ4T0EpGzto"
                      title="JAX Retina & Alzheimer’s Research"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
                    ></iframe>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="partner-section">
          <Container>
            <Row>
              <Col lg={{ span: 8, offset: 2 }}>
                <h2 className="partner-section__heading">Scientists Leading the Charge</h2>
                <ul className="partner-list">
                  <li>
                    <strong>Gareth Howell, Ph.D.</strong>
                    Professor and Diana Davis Spencer Foundation Chair for Glaucoma Research. His team links
                    changes in the retina to early dementia risk—positioning eye exams as a potential biomarker for Alzheimer&apos;s.
                  </li>
                  <li>
                    <strong>Gregory Carter, Ph.D.</strong>
                    Computational biologist developing data-driven tools to pinpoint genetic variants
                    and biomarkers associated with Alzheimer&apos;s, enabling earlier detection and better-targeted therapies.
                  </li>
                  <li>
                    <strong>Catherine Kaczorowski, Ph.D.</strong>
                    Neuroscientist identifying “biomarkers of resilience”—genes and molecules that
                    appear to protect some people from cognitive decline despite Alzheimer&apos;s pathology.
                  </li>
                </ul>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="partner-section" id="how-we-partner">
          <Container>
            <Row>
              <Col lg={{ span: 8, offset: 2 }}>
                <h2 className="partner-section__heading">
                  How the Gail Thompson Foundation Will Partner with JAX
                </h2>

                <p className="partner-section__body">
                  The Gail Thompson Foundation is committed to putting donations
                  to work right here in Maine. With Jackson Laboratory, our goal
                  is to help move promising early-detection research closer to
                  real tools that patients and doctors can use.
                </p>

                <ul className="partner-list">
                  <li>
                    <strong>Support early detection science.</strong> Direct a portion of funds to projects that validate retinal biomarkers and other
                    non-invasive tools that can flag Alzheimer&apos;s risk years earlier.
                  </li>
                  <li>
                    <strong>Bridge lab discoveries to clinics.</strong> Help resource pilot studies, prototype development, and data analysis that
                    move findings from mouse models and big-data pipelines toward human use.
                  </li>
                  <li>
                    <strong>Keep Maine families at the center.</strong> As results mature, prioritize education and access for local providers,
                    caregivers, and patients across Maine.
                  </li>
                </ul>

                <p className="partner-section__body">
                  Every gift to the Gail Thompson Foundation helps us stand
                  alongside JAX&apos;s scientists as they look for earlier,
                  kinder ways to detect and treat Alzheimer&apos;s disease.
                </p>

                <div className="partner-section__cta">
                  <Button variant="primary" onClick={() => openDonate({ startOnContact: false })}>
                    Donate to support JAX research
                  </Button>
                  <a href="/" className="partner-section__back-link">
                    ← Back to main fundraiser
                  </a>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="partner-section partner-section--alt">
          <Container>
            <Row>
              <Col lg={{ span: 8, offset: 2 }}>
                <h2 className="partner-section__heading">Dive Deeper into the Science</h2>
                <div className="partner-section__body">
                  <ul className="partner-list">
                    <li>
                      <a
                        href="https://www.jax.org/news-and-insights/2025/may/more-than-meets-the-eye"
                        target="_blank"
                        rel="noreferrer"
                        className="partner-hero__link"
                      >
                        More than meets the eye — genetics of retinal aging and brain health →
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.jax.org/personalized-medicine/alzheimers-research"
                        target="_blank"
                        rel="noreferrer"
                        className="partner-hero__link"
                      >
                        Stopping Alzheimer’s before it starts — JAX Alzheimer’s research overview →
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.jax.org/news-and-insights/2025/august/could-routine-eye-exams-reveal-early-signs-of-alzheimer-s-"
                        target="_blank"
                        rel="noreferrer"
                        className="partner-hero__link"
                      >
                        Could routine eye exams reveal early signs of Alzheimer’s? →
                      </a>
                    </li>
                  </ul>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      </main>
      <Footer />
    </>
  );
}
